import React from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Table } from "react-bootstrap";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import moment from "moment-timezone";

import { getProcessingSpeed, getOrderStatus, getMainProduct, handleProductStatus, getLogOfStatuses } from "_helpers";

import Title from "containers/Title";

const Container = styled.div`
  width: 100%;
  max-width: 1180px;
`;
const Header = styled.thead`
  height: 80px;
`;
const HeaderRow = styled.tr`
  vertical-align: middle;
  border-top: 1px solid #ecedf1;
  border-bottom: 1px solid #ecedf1;
`;
const HeaderRowEmpty = styled.tr`
  height: 20px;
`;
const HeaderCell = styled.th`
  text-align: center;
  font-style: normal;
  font-weight: bold;
  font-size: 12px;
  line-height: 15px;
  color: #7b848d;

  ${({ mwidth }) =>
    mwidth &&
    css`
      min-width: ${mwidth}px;
      max-width: ${mwidth}px;
    `}

  ${({ theme: { mobile } }) =>
    !mobile &&
    css`
      @media (max-width: 1350px) {
        min-width: 0px;
        max-width: auto;
      }
    `}
`;
const HeaderCellLeft = styled(HeaderCell)`
  text-align: left;
`;
const Body = styled.tbody`
  border: none !important;
`;
const BodyRow = styled(HeaderRow)`
  border-top: none;
  height: 77px;
  cursor: pointer;
  ${({ index }) =>
    index % 2 !== 0 &&
    css`
      background-color: #fafaff;
    `}
`;
const BodyCell = styled.td`
  text-align: center;
  font-style: normal;
  font-size: 12px;
  color: #394149;

  ${({ mwidth }) =>
    mwidth &&
    css`
      min-width: ${mwidth}px;
      max-width: ${mwidth}px;
    `}

  ${({ theme: { mobile } }) =>
    !mobile &&
    css`
      @media (max-width: 1350px) {
        min-width: 0px;
        max-width: auto;
      }
    `}
`;
const BodyCellLeft = styled(BodyCell)`
  text-align: left;
`;
const BodyDateCell = styled(BodyCell)`
  text-transform: uppercase;
`;
const Status = styled.div`
  color: #44cdc8;
  background: #e3f8f7;
  border: 1px solid #44cdc8;
  box-sizing: border-box;
  border-radius: 3px;
  text-transform: uppercase;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;

  ${({ status }) =>
    status === "Cancelled" &&
    css`
      color: #ef9a9a;
      background: #ffebee;
      border: 1px solid #ef9a9a;
    `}

  ${({ status }) =>
    status === "Error" &&
    css`
      color: #efc09a;
      background: #fff5eb;
      border: 1px solid #efc09a;
    `}
`;

const LinkText = styled(Link)`
  color: #03589c;
`;

function TableRow(props) {
  const { scrollToContent } = props;

  const navigate = useNavigate();

  const { processingStatusesObj } = useSelector(({ constant }) => constant);

  const mainProduct = getMainProduct(props.products);
  const productStatus = getOrderStatus(props.products);
  const logOfStatuses = getLogOfStatuses(props.products);
  const { status: handledStatus } = handleProductStatus({ status: productStatus, logOfStatuses });
  const status = (processingStatusesObj && processingStatusesObj[handledStatus]) || handledStatus || "N/A";

  const handleOpenOrder = () => {
    scrollToContent();
    navigate(`/orders/${props.uid}`);
  };

  return (
    <BodyRow index={props.index} onClick={handleOpenOrder}>
      <BodyCell mwidth={100}>
        <LinkText onClick={(e) => e.stopPropagation()} to={props?.uid ? `/orders/${props?.uid}` : "#"} target="_blank">
          {props?.crmOrderId || "N/A"}
        </LinkText>
      </BodyCell>
      <BodyDateCell mwidth={130}>
        {moment(mainProduct.paymentDate, "YYYY-MM-DDThh:mm:ss").format("MMM DD, YYYY")}
      </BodyDateCell>
      <BodyCellLeft>
        {props.productCodes
          .map(({ name }) => (name === "Trademark registration V2" ? "Trademark registration" : name))
          .join(", ")}
      </BodyCellLeft>
      <BodyCell mwidth={200}>
        <Status status={status}>{status}</Status>
      </BodyCell>
      <BodyCell mwidth={150}>{getProcessingSpeed(props.products)}</BodyCell>
    </BodyRow>
  );
}

TableRow.propTypes = {
  uid: PropTypes.string.isRequired,
  crmOrderId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  products: PropTypes.array.isRequired,
  productCodes: PropTypes.array.isRequired,
  scrollToContent: PropTypes.func,
};

function OrderTable({ orderToShow, scrollToContent }) {
  return (
    <>
      <Title>Orders</Title>
      <Container>
        <Table maxwidth={1180}>
          <Header>
            <HeaderRow>
              <HeaderCell mwidth={100}>ID</HeaderCell>
              <HeaderCell mwidth={130}>DATE PLACED</HeaderCell>
              <HeaderCellLeft>ORDER DETAILS</HeaderCellLeft>
              <HeaderCell mwidth={200}>STATUS</HeaderCell>
              <HeaderCell mwidth={150}>PROCESSING SPEED</HeaderCell>
            </HeaderRow>
            <HeaderRowEmpty />
          </Header>
          <Body>
            {orderToShow.map((order, idx) => (
              <TableRow key={idx} index={idx} {...order} scrollToContent={scrollToContent} />
            ))}
          </Body>
        </Table>
      </Container>
    </>
  );
}

OrderTable.propTypes = {
  orderToShow: PropTypes.arrayOf(PropTypes.object),
  scrollToContent: PropTypes.func,
};

export default OrderTable;
